import { Flex, Grid, Label, Text } from '@approvalmax/ui/src/components';
import { domain } from 'modules/data';
import { ComponentType, memo, MemoExoticComponent, useCallback, useMemo, useRef, useState } from 'react';
import { useHover } from 'react-use';

import { useIsInRoles } from '../../hooks/useIsInRoles';
import { Loading } from './BankAccountCard.loading';
import { messages } from './BankAccountCard.messages';
import { Root } from './BankAccountCard.styles';
import type { BankAccountCardProps, ChildrenComponents } from './BankAccountCard.types';
import {
    CardControls,
    RenameBankAccountForm,
    XeroAccountConnectRequest,
    XeroAccountDetails,
    XeroIntegrationDisconnectedNotice,
} from './components';

export const BankAccountCard = memo<BankAccountCardProps>((props) => {
    const {
        id,
        logoImage,
        nickname,
        bankName,
        balance,
        connected,
        xeroAccount,
        onRematchXeroBankAccount,
        status,
        ...otherProps
    } = props;

    const [showRenameForm, setShowRenameForm] = useState(false);
    const disconnectedColor = connected ? 'midnight100' : 'midnight60';

    const isManager = useIsInRoles(domain.CompanyUserRole.Manager);
    const rootRef = useRef<HTMLDivElement | null>(null);

    const fields = useMemo(() => {
        const fields: ('bankAccountNumber' | 'sortCode' | 'iban')[] = [];

        if (otherProps.bankAccountNumber) {
            fields.push('bankAccountNumber');
        }

        if (otherProps.sortCode) {
            fields.push('sortCode');
        }

        if (otherProps.iban) {
            fields.push('iban');
        }

        return fields;
    }, [otherProps.bankAccountNumber, otherProps.iban, otherProps.sortCode]);

    const handleFormClose = useCallback(() => {
        setShowRenameForm(false);
    }, []);

    const [hoverable] = useHover((hover) => (
        <Root
            bordered
            borderColor='midnight60'
            radius='medium'
            spacing='24 32'
            // to behave as masonry-like within grid
            height='max-content'
            role='listitem'
            $disconnected={!connected}
            ref={rootRef}
            data-testid={`bank-account-${id}`}
        >
            <Grid gap={24}>
                <Flex direction='column' spacing='16'>
                    <Grid gap={16} gridTemplateColumns='max-content auto'>
                        {logoImage}

                        {typeof nickname === 'string' && showRenameForm ? (
                            <RenameBankAccountForm
                                id={id}
                                nickname={nickname}
                                rootRef={rootRef}
                                onClose={handleFormClose}
                            />
                        ) : (
                            <div>
                                <Flex spacing='8' alignItems='stretch' justifyContent='space-between'>
                                    <Label color={connected ? 'midnight70' : 'midnight60'}>{bankName}</Label>

                                    {connected || (
                                        <Text font='label' fontSize='xsmall' fontWeight='medium' color='red100'>
                                            {messages.disconnected.toUpperCase()}
                                        </Text>
                                    )}
                                </Flex>

                                <Grid gap={8} gridTemplateColumns={isManager ? 'auto max-content' : undefined}>
                                    <Text
                                        title={typeof nickname === 'string' ? nickname : undefined}
                                        font='headline'
                                        fontSize='xsmall'
                                        color={connected ? 'midnight90' : 'midnight60'}
                                        ellipsis={1}
                                    >
                                        {nickname}
                                    </Text>

                                    {isManager && (
                                        <CardControls
                                            id={id}
                                            nickname={nickname}
                                            connected={connected}
                                            hide={!hover}
                                            consentStatus={status}
                                            onRematchXeroBankAccount={onRematchXeroBankAccount}
                                            setShowRenameForm={setShowRenameForm}
                                        />
                                    )}
                                </Grid>
                            </div>
                        )}
                    </Grid>

                    {balance && (
                        <div>
                            <Label>{messages.balance}</Label>

                            <Text font='label' fontSize='large' fontWeight='bold' color={disconnectedColor}>
                                {balance}
                            </Text>
                        </div>
                    )}

                    <Grid gridTemplateColumns='auto max-content' gap={16}>
                        <Flex justifyContent='space-between' spacing='8 16'>
                            {fields.map((prop) => (
                                <div key={prop}>
                                    <Label>{messages[prop]}</Label>

                                    <Text font='label' fontSize='small' color={disconnectedColor}>
                                        {otherProps[prop]}
                                    </Text>
                                </div>
                            ))}
                        </Flex>

                        <div>
                            <Label>{messages.currencyCode}</Label>

                            <Text font='label' fontSize='small' color={disconnectedColor}>
                                {otherProps.currencyCode}
                            </Text>
                        </div>
                    </Grid>
                </Flex>

                {xeroAccount}
            </Grid>
        </Root>
    ));

    return hoverable;
}) as MemoExoticComponent<ComponentType<BankAccountCardProps>> & ChildrenComponents;

BankAccountCard.displayName = 'BankAccountCard';
BankAccountCard.Loading = Loading;
BankAccountCard.XeroAccountDetails = XeroAccountDetails;
BankAccountCard.XeroAccountConnectRequest = XeroAccountConnectRequest;
BankAccountCard.XeroIntegrationDisconnectedNotice = XeroIntegrationDisconnectedNotice;
